import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useProperty, useSlotGroup, useTemplateContext } from '@vibesites/runtime/react';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Page hero image' },
  },
};

// Hyperlocal h1_title / h2_subtitle are plain strings. Wrap them so
// RichTextContent keeps the same <p> shape, and escape markup characters.
function toRichTextDefault(text: string | undefined | null, fallback: string): string {
  if (!text) return fallback;
  const escaped = String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return `<p>${escaped}</p>`;
}

// The generator stores "no value" as the literal strings "false", "null",
// "none", or empty — all truthy, none a URL.
function pickImage(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  if (lower === 'false' || lower === 'true' || lower === 'null' || lower === 'none' || lower === 'undefined') {
    return null;
  }
  return trimmed;
}

export function HyperlocalHero() {
  const property = useProperty();
  const hl = useTemplateContext()?.hyperlocal;

  const bgImage = useSlotGroup('hyperlocalhero.bg_image', bgImageDef);

  // Slot override, then this page's query image, then the property photo.
  const displayImage =
    pickImage(bgImage.image) ||
    pickImage(hl?.featured_image) ||
    pickImage(property.property_featured_image) ||
    '';
  const slotAlt = bgImage.alt && bgImage.alt !== 'Page hero image' ? bgImage.alt : '';
  const displayAlt =
    slotAlt ||
    hl?.h1_title ||
    property.property_featured_image_media?.alt_text ||
    'Page hero image';

  const titleDefault = toRichTextDefault(hl?.h1_title, '<p>Hyperlocal Page Title</p>');
  const subtitleDefault = toRichTextDefault(hl?.h2_subtitle, '<p>Explore apartments in your area</p>');

  return (
    <section className="relative w-full h-[70vh] min-h-[450px] max-h-[700px] overflow-hidden">
      <EditableSlotGroup groupPath="hyperlocalhero.bg_image" definition={bgImageDef}>
        <div className="absolute inset-0">
          {displayImage ? (
            <VibesitesImage
              url={displayImage}
              alt={displayAlt}
              sizes="100vw"
              className="w-full h-full object-cover"
              style={{
                animation: 'hlKenBurns 30s ease-in-out infinite alternate',
                willChange: 'transform',
                transformOrigin: 'bottom right',
              }}
            />
          ) : (
            <div className="w-full h-full bg-[var(--vibe-color-text)]" />
          )}
        </div>
      </EditableSlotGroup>

      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/35 to-black/20" />

      <div className="hl-copy relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-6 sm:px-8">
        <RichTextContent
          path="hyperlocalhero.title"
          label="Title"
          defaultValue={titleDefault}
          as="h1"
          className="hl-title hl-entrance hl-entrance-1 mb-5"
        />
        <RichTextContent
          path="hyperlocalhero.subtitle"
          label="Subtitle"
          defaultValue={subtitleDefault}
          as="h2"
          className="hl-entrance hl-entrance-2 font-body text-lg sm:text-xl md:text-2xl max-w-3xl leading-relaxed opacity-90 drop-shadow-md font-normal"
        />
      </div>

      <style>{`
        /* Fixed header overlays this hero. Top padding clears the utility
           strip and nav (plus a special-offer banner when one is showing)
           so the title and subtitle start below it. */
        .hl-copy {
          box-sizing: border-box;
          padding-top: calc(var(--offer-banner-height, 0px) + 8.5rem);
          padding-bottom: 2.5rem;
        }
        @media (min-width: 768px) {
          .hl-copy { padding-top: calc(var(--offer-banner-height, 0px) + 9.5rem); }
        }
        @media (min-width: 1024px) {
          .hl-copy { padding-top: calc(var(--offer-banner-height, 0px) + 10.5rem); }
        }
        /* Match HeroSolidBG's .hero-solid-title exactly so the two page
           heroes read as the same title. RichTextContent wraps the text in
           a <p>, so the inner elements must inherit these styles. */
        .hl-title {
          font-family: "Rubik", sans-serif;
          font-size: 30px;
          font-weight: 700;
          line-height: 1.05;
          letter-spacing: -0.01em;
          color: #fff;
          text-shadow: 0 2px 18px rgba(0, 0, 0, 0.45);
          max-width: 24ch;
        }
        .hl-title p,
        .hl-title span {
          font: inherit;
          color: inherit;
          text-transform: inherit;
          text-shadow: inherit;
          margin: 0;
        }
        @media (min-width: 768px) {
          .hl-title { font-size: 62px; }
        }
        @media (min-width: 1024px) {
          .hl-title { font-size: 64px; }
        }
        @keyframes hlKenBurns {
          0% { transform: scale(1.01) translate(0, 0); }
          100% { transform: scale(1.05) translate(-0.8%, -0.5%); }
        }
        @keyframes hlEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hl-entrance {
          animation: hlEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hl-entrance-1 { animation-delay: 0.2s; }
        .hl-entrance-2 { animation-delay: 0.45s; }
        @media (prefers-reduced-motion: reduce) {
          .hl-entrance { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HyperlocalHero',
  displayName: 'Hyperlocal Hero',
  tokens: {
    'hyperlocalhero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Hyperlocal Page Title</p>',
    },
    'hyperlocalhero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Explore apartments in your area</p>',
    },
  },
  slotGroups: {
    'hyperlocalhero.bg_image': {
      label: 'Background Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultValue: '' },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Page hero image' },
      },
    },
  },
};

(HyperlocalHero as any).contentSchema = contentSchema;

export default HyperlocalHero;
